/**
 * Reproductor de Audio para Etereal
 * Funcionalidad completa para el reproductor de audio personalizado
 */

class AudioPlayer {
  constructor(container) {
    this.container = container;
    this.audio = container.querySelector('.audio-player__audio');
    this.playBtn = container.querySelector('.audio-player__play-btn');
    this.playIcon = container.querySelector('.audio-player__play-icon');
    this.pauseIcon = container.querySelector('.audio-player__pause-icon');
    this.progressBar = container.querySelector('.audio-player__progress-bar');
    this.progressFill = container.querySelector('.audio-player__progress-fill');
    this.progressHandle = container.querySelector('.audio-player__progress-handle');
    this.currentTimeEl = container.querySelector('.audio-player__current-time');
    this.totalTimeEl = container.querySelector('.audio-player__total-time');
    this.volumeBtn = container.querySelector('.audio-player__volume-btn');
    this.volumeIcon = container.querySelector('.audio-player__volume-icon');
    this.muteIcon = container.querySelector('.audio-player__mute-icon');
    
    this.isPlaying = false;
    this.isMuted = false;
    this.isDragging = false;
    this.previousVolume = 1;
    
    this.init();
  }

  init() {
    if (!this.audio) return;
    
    this.bindEvents();
    this.updateTotalTime();
    this.setInitialState();
  }

  bindEvents() {
    // Eventos del botón play/pause
    this.playBtn?.addEventListener('click', () => this.togglePlay());
    
    // Eventos del audio
    this.audio.addEventListener('loadstart', () => this.setLoadingState(true));
    this.audio.addEventListener('canplay', () => this.setLoadingState(false));
    this.audio.addEventListener('loadedmetadata', () => this.updateTotalTime());
    this.audio.addEventListener('timeupdate', () => this.updateProgress());
    this.audio.addEventListener('ended', () => this.onAudioEnded());
    this.audio.addEventListener('error', (e) => this.onAudioError(e));
    
    // Eventos de la barra de progreso
    this.progressBar?.addEventListener('click', (e) => this.seekTo(e));
    this.progressBar?.addEventListener('mousedown', (e) => this.startDrag(e));
    document.addEventListener('mousemove', (e) => this.onDrag(e));
    document.addEventListener('mouseup', () => this.endDrag());
    
    // Eventos del botón de volumen
    this.volumeBtn?.addEventListener('click', () => this.toggleMute());
    
    // Eventos de teclado para accesibilidad
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  togglePlay() {
    if (!this.audio) return;
    
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  async play() {
    try {
      await this.audio.play();
      this.isPlaying = true;
      this.updatePlayState();
      this.showNotification('Reproduciendo audio', 'success');
    } catch (error) {
      console.error('Error al reproducir audio:', error);
      this.showNotification('Error al reproducir el audio', 'error');
    }
  }

  pause() {
    this.audio.pause();
    this.isPlaying = false;
    this.updatePlayState();
  }

  updatePlayState() {
    this.container.setAttribute('data-playing', this.isPlaying);
    
    if (this.playIcon && this.pauseIcon) {
      this.playIcon.style.display = this.isPlaying ? 'none' : 'block';
      this.pauseIcon.style.display = this.isPlaying ? 'block' : 'none';
    }
    
    // Actualizar aria-label para accesibilidad
    if (this.playBtn) {
      this.playBtn.setAttribute('aria-label', this.isPlaying ? 'Pausar audio' : 'Reproducir audio');
    }
  }

  updateProgress() {
    if (!this.audio || this.isDragging) return;
    
    const currentTime = this.audio.currentTime;
    const duration = this.audio.duration;
    
    if (duration > 0) {
      const progress = (currentTime / duration) * 100;
      this.updateProgressBar(progress);
    }
    
    this.updateCurrentTime(currentTime);
  }

  updateProgressBar(progress) {
    if (this.progressFill) {
      this.progressFill.style.width = `${progress}%`;
    }
    
    if (this.progressHandle) {
      this.progressHandle.style.left = `${progress}%`;
    }
  }

  updateCurrentTime(currentTime) {
    if (this.currentTimeEl) {
      this.currentTimeEl.textContent = this.formatTime(currentTime);
    }
  }

  updateTotalTime() {
    if (this.audio && this.totalTimeEl) {
      const duration = this.audio.duration;
      if (duration && !isNaN(duration)) {
        this.totalTimeEl.textContent = this.formatTime(duration);
      }
    }
  }

  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '00:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  seekTo(event) {
    if (!this.audio || this.isDragging) return;
    
    const rect = this.progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const progress = clickX / rect.width;
    const newTime = progress * this.audio.duration;
    
    if (newTime >= 0 && newTime <= this.audio.duration) {
      this.audio.currentTime = newTime;
      this.updateProgressBar(progress * 100);
    }
  }

  startDrag(event) {
    this.isDragging = true;
    this.progressBar.classList.add('dragging');
    this.onDrag(event);
  }

  onDrag(event) {
    if (!this.isDragging || !this.audio) return;
    
    const rect = this.progressBar.getBoundingClientRect();
    const dragX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const progress = dragX / rect.width;
    
    this.updateProgressBar(progress * 100);
    
    // Actualizar tiempo durante el arrastre
    const newTime = progress * this.audio.duration;
    this.updateCurrentTime(newTime);
  }

  endDrag() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.progressBar.classList.remove('dragging');
    
    // Aplicar el tiempo final
    if (this.audio) {
      const progress = parseFloat(this.progressFill.style.width) / 100;
      const newTime = progress * this.audio.duration;
      this.audio.currentTime = newTime;
    }
  }

  toggleMute() {
    if (!this.audio) return;
    
    if (this.isMuted) {
      this.audio.volume = this.previousVolume;
      this.isMuted = false;
    } else {
      this.previousVolume = this.audio.volume;
      this.audio.volume = 0;
      this.isMuted = true;
    }
    
    this.updateMuteState();
  }

  updateMuteState() {
    this.container.setAttribute('data-muted', this.isMuted);
    
    if (this.volumeIcon && this.muteIcon) {
      this.volumeIcon.style.display = this.isMuted ? 'none' : 'block';
      this.muteIcon.style.display = this.isMuted ? 'block' : 'none';
    }
    
    // Actualizar aria-label para accesibilidad
    if (this.volumeBtn) {
      this.volumeBtn.setAttribute('aria-label', this.isMuted ? 'Activar sonido' : 'Silenciar');
    }
  }

  setLoadingState(loading) {
    this.container.setAttribute('data-loading', loading);
  }

  setInitialState() {
    this.container.setAttribute('data-playing', 'false');
    this.container.setAttribute('data-muted', 'false');
    this.container.setAttribute('data-loading', 'false');
  }

  onAudioEnded() {
    this.isPlaying = false;
    this.updatePlayState();
    this.updateProgressBar(0);
    this.audio.currentTime = 0;
    this.showNotification('Audio finalizado', 'info');
  }

  onAudioError(error) {
    console.error('Error en el audio:', error);
    this.setLoadingState(false);
    this.showNotification('Error al cargar el audio. Verifica el archivo.', 'error');
  }

  handleKeyboard(event) {
    switch (event.key) {
      case ' ':
      case 'Enter':
        if (event.target === this.playBtn) {
          event.preventDefault();
          this.togglePlay();
        }
        break;
      case 'ArrowLeft':
        if (this.audio) {
          this.audio.currentTime = Math.max(0, this.audio.currentTime - 10);
        }
        break;
      case 'ArrowRight':
        if (this.audio) {
          this.audio.currentTime = Math.min(this.audio.duration, this.audio.currentTime + 10);
        }
        break;
      case 'm':
      case 'M':
        this.toggleMute();
        break;
    }
  }

  showNotification(message, type = 'info') {
    // Integración con el sistema de notificaciones existente si está disponible
    if (window.showNotification) {
      window.showNotification(message, type);
    } else {
      // Fallback simple
      console.log(`[Audio Player ${type.toUpperCase()}]: ${message}`);
    }
  }

  // Método para destruir el reproductor y limpiar eventos
  destroy() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    
    // Remover event listeners
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.endDrag);
  }
}

// Inicialización automática cuando el DOM está listo
document.addEventListener('DOMContentLoaded', function() {
  const audioPlayers = document.querySelectorAll('[data-audio-player]');
  
  audioPlayers.forEach(container => {
    new AudioPlayer(container);
  });
});

// Reinicializar reproductores después de actualizaciones AJAX (para Shopify)
document.addEventListener('shopify:section:load', function(event) {
  const audioPlayers = event.target.querySelectorAll('[data-audio-player]');
  
  audioPlayers.forEach(container => {
    new AudioPlayer(container);
  });
});

// Exportar para uso global si es necesario
window.AudioPlayer = AudioPlayer;
